import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Store', path: '/store' },
    { name: 'Blog', path: '/blog' },
    { name: 'Careers', path: '/careers' },
    { name: 'Contact', path: '/contact' },
    { name: 'Testimonials', path: '/testimonials' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className={`fixed w-full top-0 z-50 transition-all duration-500 px-4 md:px-8 ${scrolled ? 'pt-4' : 'pt-8'}`}>
      <div className="max-w-[1400px] mx-auto">
        <div className={`transition-all duration-500 flex justify-between items-center px-8 md:px-12 h-20 rounded-full border border-white/20 ${scrolled
          ? 'bg-white/80 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.08)]'
          : 'bg-transparent'
          }`}>
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img
              src="/rita_logo_2-removebg-preview.png"
              alt="Rita Logo"
              className={`h-16 w-auto object-contain transition-all duration-500 group-hover:scale-110 ${scrolled ? '' : 'brightness-110'}`}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-lg font-bold transition-all hover:text-[#0c513f] relative group/link ${isActive(link.path)
                  ? 'text-[#0c513f]'
                  : 'text-secondary-600'
                  }`}
              >
                {link.name}
                <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-[#0c513f] transition-all duration-300 group-hover/link:w-full ${isActive(link.path) ? 'w-full' : ''}`}></span>
              </Link>
            ))}
            <Link
              to="/login"
              className="text-[#0c513f] hover:text-white border-2 border-[#0c513f] hover:bg-[#0c513f] px-8 py-3 rounded-full font-bold tracking-tight transition-all hover:scale-105 active:scale-95 ml-4"
            >
              Login
            </Link>
            <Link
              to="/app"
              className="bg-[#0c513f] hover:bg-[#073b2e] text-white px-10 py-3.5 rounded-full font-black tracking-tight transition-all hover:scale-105 active:scale-95 shadow-[0_8px_20px_rgba(12,81,63,0.3)] ml-4"
            >
              Get App
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-secondary-900 p-2 hover:bg-[#0c513f]/5 rounded-full transition-colors"
          >
            {isOpen ? <X size={32} /> : <Menu size={32} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="lg:hidden absolute top-full left-4 right-4 mt-4 bg-white/95 backdrop-blur-2xl rounded-[3rem] p-8 shadow-2xl border border-white/20 animate-fade-in-up">
          <div className="space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-8 py-5 rounded-3xl text-xl font-bold transition-all ${isActive(link.path)
                  ? 'bg-[#0c513f] text-white'
                  : 'text-secondary-600 hover:bg-secondary-50'
                  }`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/login"
              onClick={() => setIsOpen(false)}
              className="block border-2 border-[#0c513f] text-[#0c513f] hover:bg-[#0c513f] hover:text-white px-8 py-5 rounded-3xl font-bold text-xl text-center mt-4 transition-colors"
            >
              Login
            </Link>
            <Link
              to="/app"
              onClick={() => setIsOpen(false)}
              className="block bg-[#0c513f] text-white px-8 py-6 rounded-3xl font-black text-xl text-center mt-4 shadow-xl"
            >
              Get the App
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;


